import React, {FC, memo, useState} from "react";
import {FormikProps} from "formik";
import {Button} from "antd";
import moment from "moment";
import uuid from "uuid";
import {GetBookingsByCourseIdWithCourseLessonBookingsQuery, PaymentType, UpdateCourseAndBookingsInput, UpdateCourseAndBookingsResult} from "../../../../generated/graphql";
import css from "./PaymentLists.less";
import {PreviewBookingType} from "../PreviewBooking";
import TotalPriceinteractive from "./TotalPriceinteractive";
import RenderMonthlyPaymentEntry from "./RenderMonthlPaymentEntry";
import SwitchMonthlyPayment from "./SwitchMonthlyPayment";
import EditPaymentView from "./EditPaymentView";
import {calculateNextDueDate} from "../../../bookings/lessonBooking/CreateLessonBooking/LessonBookingSteps/LessonBookingSummaryComps/PricePreview/components";
import {ScheduledPaymentType as HelperScheduledPaymentType} from "../helper";

export type ScheduledPaymentType = GetBookingsByCourseIdWithCourseLessonBookingsQuery["bookingsByCourseIdWithCourseLessonBookings"][0]["scheduledPayments"][number];

type PaymentsInteractiveProps = {
    booking: PreviewBookingType;
    // values: UpdateCourseAndBookingsInput;
    formikProps: FormikProps<UpdateCourseAndBookingsInput>;
    preview?: UpdateCourseAndBookingsResult;
    disabled?: boolean;
    changedPaymentIds?: {changedIds: Set<string>; addedIds: Set<string>; removedIds: Set<string>};
    originalBooking?: GetBookingsByCourseIdWithCourseLessonBookingsQuery["bookingsByCourseIdWithCourseLessonBookings"][0];
    isTotalPriceChanged?: boolean;
};

const PaymentsInteractive: FC<PaymentsInteractiveProps> = ({booking, formikProps, preview, disabled = false, changedPaymentIds, originalBooking, isTotalPriceChanged = false}) => {
    const [editPaymentIndex, setEditPaymentIndex] = useState<number | null>(null);
    const [lastAddedPaymentId, setLastAddedPaymentId] = useState<string | null>(null);
    const {values} = formikProps;
    const currentBookingsData = formikProps.values.updatedBookingsData ?? [];
    const currentBooking = currentBookingsData.find((bookingX) => bookingX.id === booking.id);
    const currentScheduledPayments = currentBooking?.scheduledPayments ?? booking.scheduledPayments ?? [];
    const bookingId = booking.id;
    const paymentType = currentBooking?.paymentType;

    // Bricht die Bearbeitung ab
    const handleCancelEdit = (payment: PreviewBookingType["scheduledPayments"][number]) => {
        // Wenn dieses Payment gerade hinzugefügt wurde und noch nicht gespeichert ist
        if (lastAddedPaymentId === payment.id) {
            console.log("Lösche neu hinzugefügtes Payment:", payment.id);
            // Entferne dieses Payment aus der Liste
            const filteredPayments = booking.scheduledPayments?.filter((p) => p.id !== payment.id) ?? [];

            formikProps.setFieldValue("scheduledPayments", filteredPayments);
            // Zurücksetzen des lastAddedPaymentId
            setLastAddedPaymentId(null);
        }
        // Bearbeitungsmodus beenden
        setEditPaymentIndex(null);
    };

    const handleSave = (payment: ScheduledPaymentType, rowIndex: number) => {
        console.log("handleSave", payment, rowIndex);
        const currentPayments = booking.scheduledPayments ?? [];
        const newPayments = [...currentPayments];

        newPayments[rowIndex] = payment;

        if (currentBooking) {
            currentBooking.scheduledPayments = newPayments;
            const updatedBookingsData = [...currentBookingsData];

            updatedBookingsData.forEach((bookingX) => {
                if (bookingX.id === booking.id) {
                    bookingX.scheduledPayments = newPayments;
                }
            });

            formikProps.setFieldValue("updatedBookingsData", updatedBookingsData);
        }

        // formikProps.setFieldValue("updatedBookingsData", currentBookingsData);
        setEditPaymentIndex(null);
    };

    const handleSaveNewPayment = (payment: ScheduledPaymentType) => {
        if (!currentBooking) {
            return;
        }

        currentBooking.scheduledPayments = payment;
        const updatedBookingsData = [...currentBookingsData];

        updatedBookingsData.forEach((bookingX) => {
            if (bookingX.id === bookingId) {
                bookingX.scheduledPayments = payment;
            }
        });

        formikProps.setFieldValue("updatedBookingsData", updatedBookingsData);
    };

    const addNewPayment = () => {
        const lastPayment = currentScheduledPayments[currentScheduledPayments.length - 1];
        const lastDue = moment(lastPayment?.paymentDue);
        const newDue = calculateNextDueDate(lastDue);
        const newPaymentId = uuid();
        const newPayment: ScheduledPaymentType = {
            id: newPaymentId,
            paymentAmount: lastPayment.paymentAmount,
            paymentDue: newDue.toDate(),
            type: "Standard",
        };

        const updatedScheduledPayments = [...currentScheduledPayments, newPayment].sort((a, b) => moment(a.paymentDue).valueOf() - moment(b.paymentDue).valueOf());

        handleSaveNewPayment(updatedScheduledPayments);
    };

    // Helper: Erstelle einen eindeutigen Key für eine Rate (muss mit helper.tsx übereinstimmen)
    const normalizeDate = (date: Date | string): number => {
        const d = typeof date === "string" ? new Date(date) : date;
        const normalized = new Date(d.getFullYear(), d.getMonth(), d.getDate());

        return normalized.getTime();
    };

    const getPaymentKey = (payment: ScheduledPaymentType | HelperScheduledPaymentType): string => {
        if (payment.id) {
            return payment.id;
        }
        const dueDate = normalizeDate(payment.paymentDue);
        const amount = Math.round((payment.paymentAmount ?? 0) * 100) / 100;

        return `${dueDate}-${amount}`;
    };

    return (
        <div>
            <SwitchMonthlyPayment formikProps={formikProps} booking={booking} preview={preview} disabled={disabled} />

            {/* {currentScheduledPayments.map((rate, index) => { */}
            {booking.scheduledPayments?.map((rate, index) => {
                // console.log("=> rate:::", rate);
                // console.log("=> paymentDone:::", rate.paymentDone);
                const paymentKey = getPaymentKey(rate);
                const isChanged = changedPaymentIds?.changedIds.has(paymentKey) || changedPaymentIds?.addedIds.has(paymentKey);

                return (
                    <React.Fragment key={rate.id || `payment-${index}`}>
                        {editPaymentIndex === index ? (
                            <EditPaymentView rate={rate} rowIndex={index} onCancel={handleCancelEdit} onSave={handleSave} />
                        ) : (
                            <RenderMonthlyPaymentEntry
                                formikProps={formikProps}
                                rate={rate}
                                rowIndex={index}
                                onEdit={() => {
                                    if (!disabled) {
                                        setEditPaymentIndex(index);
                                        setLastAddedPaymentId(rate.id);
                                    }
                                }}
                                booking={booking}
                                disabled={disabled}
                                isChanged={isChanged}
                            />
                        )}
                    </React.Fragment>
                );
            })}
            {paymentType === PaymentType.Custom && !disabled && (
                <Button block size="small" onClick={addNewPayment}>
                    {"Neue Rate hinzufügen"}
                </Button>
            )}
            <TotalPriceinteractive booking={booking} values={values} isChanged={isTotalPriceChanged} />
        </div>
    );
};

export default memo(PaymentsInteractive);
